import MapIcon from '@mui/icons-material/Map';
import EvStationIcon from '@mui/icons-material/EvStation';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import ChargePointListTable from "components/charging/ChargingInfrastructureComponent/ChargePointListTable";
import DashboardCard from "components/dashboard/common/DashboardCard";
import UserContext from "Context/UserProvider";
import { ChargingInfrastructure } from "interfaces/ChargingInfrastructure";
import { useContext, useEffect, useRef, useState } from "react";
import toast from 'react-hot-toast';
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { useApiService } from 'services/ApiService/ApiService';

const mapContainerStyle = {
    width: '100%',
    height: '400px',
    cursor: 'crosshair'
};

const centerDefault = {
    lat: 51.5074,
    lng: -0.1278
};

function ChargingInfrastructureEditPage(props: any) {
    const [showChargePointDialog, setShowChargePointDialog] = useState(false);
    const [showMapDialog, setShowMapDialog] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(centerDefault);
    const [mapWasUsed, setMapWasUsed] = useState(false);

    const connectorOptions = ["Type 1", "Type 2", "CCS", "CHAdeMO", "Tesla", "Other"];

    const [newChargePoint, setNewChargePoint] = useState({
        make: '',
        model: '',
        evseId: '',
        cpmsProvider: '',
        maxRateKW: '',
        connectors: [] as { type: string; count: number }[]
    });

    const defaultChargingInfrastructure: ChargingInfrastructure = {
        uuid: null,
        addressLine: null,
        postcode: null,
        name: null,
        centreLatitude: null,
        centreLongitude: null,
        chargePoints: []

    };


    const navigate = useNavigate();
    const { userInfo } = useContext(UserContext);
    const [chargingInfrastructure, setChargingInfrastructure] = useState<ChargingInfrastructure>(defaultChargingInfrastructure);
    const currentInput = useRef() as any;
    const [isDisabled, setIsDisabled] = useState(false);
    const { fetchWithAuth } = useApiService();
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: boolean }>({});

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
        libraries: ['places']
    });

    useEffect(() => {
        setChargingInfrastructure(defaultChargingInfrastructure);
    }, []);

    function onChange(e: any) {
        const updated = { ...chargingInfrastructure, [e.target.name]: e.target.value };
        setChargingInfrastructure(updated);
        
        // If user manually edits address fields, reset the mapWasUsed flag
        if (e.target.name === 'addressLine' || e.target.name === 'postcode') {
            setMapWasUsed(false);
        }

        // Clear field error when user starts typing
        if (fieldErrors[e.target.name]) {
            setFieldErrors(prev => ({
                ...prev,
                [e.target.name]: false
            }));
        }
    }

    

    async function handleMapSelection() {
        try {
            const response = await fetchWithAuth('/ocpi/reverse-geocode', {
                method: 'POST',
                body: JSON.stringify({
                    lat: selectedLocation.lat,
                    lng: selectedLocation.lng,
                })
            });

            const data = await response.json();
            console.log("Reverse geocode response:", data);

            if (data.status === "OK" && data.results.length > 0) {
                const result = data.results[0];
                const address = result.formatted_address;
                const postcodeComponent = result.address_components.find((comp: any) =>
                    comp.types.includes("postal_code")
                );
                const postcode = postcodeComponent ? postcodeComponent.long_name : '';

                setChargingInfrastructure(prev => ({
                    ...prev,
                    addressLine: address,
                    postcode: postcode,
                    centreLatitude: selectedLocation.lat,
                    centreLongitude: selectedLocation.lng
                }));
                
                // Mark that the map was used
                setMapWasUsed(true);
            } else {
                console.log(data);
                toast.error("Unable to retrieve address details.");
            }
        } catch (error) {
            console.error("Reverse geocoding error:", error);
            toast.error("An error occurred while fetching address.");
        }

        setShowMapDialog(false);
    }

    async function getInitialMapPosition() {
        console.log("getInitialMapPosition called with:", {
            addressLine: chargingInfrastructure.addressLine,
            postcode: chargingInfrastructure.postcode,
            centreLatitude: chargingInfrastructure.centreLatitude,
            centreLongitude: chargingInfrastructure.centreLongitude
        });

        // If we have address line and postcode, try to geocode them
        if (chargingInfrastructure.addressLine && chargingInfrastructure.postcode) {
            try {
                const address = `${chargingInfrastructure.addressLine}, ${chargingInfrastructure.postcode}`;
                console.log("Attempting to geocode address:", address);
                
                const response = await fetchWithAuth('/ocpi/geocode', {
                    method: 'POST',
                    body: JSON.stringify({
                        address: address
                    })
                });

                const data = await response.json();
                console.log("Geocode response:", data);

                if (data.status === "OK" && data.results && data.results.length > 0) {
                    const result = data.results[0];
                    const lat = result.geometry.location.lat;
                    const lng = result.geometry.location.lng;
                    console.log("Geocoding successful, returning:", { lat, lng });
                    return { lat, lng };
                } else {
                    console.log("Geocoding failed - no results or invalid status");
                }
            } catch (error) {
                console.error("Geocoding error:", error);
                // Fall back to default position
            }
        } else {
            console.log("No address line or postcode available for geocoding");
        }
        
        // Fall back to existing coordinates
        if (chargingInfrastructure.centreLatitude && chargingInfrastructure.centreLongitude) {
            console.log("Using existing coordinates:", {
                lat: chargingInfrastructure.centreLatitude,
                lng: chargingInfrastructure.centreLongitude
            });
            return {
                lat: chargingInfrastructure.centreLatitude,
                lng: chargingInfrastructure.centreLongitude
            };
        }
        
        console.log("Using default coordinates:", centerDefault);
        return centerDefault;
    }

    async function openMapDialog() {
        console.log("openMapDialog called");
        const initialPosition = await getInitialMapPosition();
        console.log("Setting selectedLocation to:", initialPosition);
        setSelectedLocation(initialPosition);
        setMapWasUsed(false);
        setShowMapDialog(true);
    }

    async function updateChargingInfrastructure() {
        setIsDisabled(true);

        try {
            // Validate mandatory fields
            const requiredFields = [
                { field: 'name', label: 'Name', required: true },
                { field: 'addressLine', label: 'Address Line', required: true },
                { field: 'postcode', label: 'Postcode', required: true }
            ];

            const missingFields = requiredFields.filter(({ field }) => 
                !chargingInfrastructure[field as keyof ChargingInfrastructure] || 
                chargingInfrastructure[field as keyof ChargingInfrastructure]?.toString().trim() === ''
            );

            if (missingFields.length > 0) {
                // Set field errors for missing fields
                const newFieldErrors: { [key: string]: boolean } = {};
                requiredFields.forEach(({ field }) => {
                    newFieldErrors[field] = !chargingInfrastructure[field as keyof ChargingInfrastructure] || 
                        chargingInfrastructure[field as keyof ChargingInfrastructure]?.toString().trim() === '';
                });
                setFieldErrors(newFieldErrors);

                const fieldLabels = missingFields.map(f => f.label).join(', ');
                toast.error(`Please fill in the following required fields: ${fieldLabels}`, {
                    position: 'bottom-center',
                    duration: 5000,
                });
                setIsDisabled(false);
                return;
            }

            // Clear field errors if validation passes
            setFieldErrors({});

            let infrastructureToSave = { ...chargingInfrastructure };
            
            // If map wasn't used and we have address but no coordinates, geocode the address
            if (!mapWasUsed && 
                chargingInfrastructure.addressLine && 
                chargingInfrastructure.postcode && 
                (!chargingInfrastructure.centreLatitude || !chargingInfrastructure.centreLongitude)) {
                
                try {
                    const address = `${chargingInfrastructure.addressLine}, ${chargingInfrastructure.postcode}`;
                    console.log("Geocoding address for save:", address);
                    
                    const response = await fetchWithAuth('/ocpi/geocode', {
                        method: 'POST',
                        body: JSON.stringify({
                            address: address
                        })
                    });

                    const data = await response.json();
                    console.log("Geocode response for save:", data);

                    if (data.status === "OK" && data.results && data.results.length > 0) {
                        const result = data.results[0];
                        const lat = result.geometry.location.lat;
                        const lng = result.geometry.location.lng;
                        
                        infrastructureToSave = {
                            ...infrastructureToSave,
                            centreLatitude: lat,
                            centreLongitude: lng
                        };
                        
                        console.log("Geocoded coordinates for save:", { lat, lng });
                    } else {
                        console.log("Geocoding failed for save - no results or invalid status");
                    }
                } catch (error) {
                    console.error("Geocoding error during save:", error);
                    // Continue with save even if geocoding fails
                }
            }

            const response = await fetchWithAuth('/charging-infrastructure/save', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    chargingInfrastructure: infrastructureToSave,
                    businessUuid: userInfo.businessUuid
                })
            });

            if (response.ok) {
                toast.success('Charging Infrastructure saved', {
                    position: 'bottom-center',
                    duration: 3000,
                });
                navigate("/share/business/charging-infrastructure/dwellings");
            } else {
                // Try to get the error message from the response
                let errorMessage = 'Failed to save location';
                try {
                    const errorData = await response.json();
                    if (errorData.message) {
                        errorMessage = errorData.message;
                    } else if (errorData.error) {
                        errorMessage = errorData.error;
                    } else if (typeof errorData === 'string') {
                        errorMessage = errorData;
                    }
                } catch (parseError) {
                    console.error('Error parsing response:', parseError);
                    // Keep the default error message if parsing fails
                }
                
                toast.error(errorMessage, {
                    position: 'bottom-center',
                    duration: 5000,
                });
            }
        } catch (error) {
            console.error('Error saving charging infrastructure:', error);
            toast.error('Failed to save location', {
                position: 'bottom-center',
                duration: 3000,
            });
        } finally {
            setIsDisabled(false);
        }
    }

    const getCountForType = (type: string) => {
        const match = newChargePoint.connectors.find((c) => c.type === type);
        return match ? ` (${match.count})` : '';
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <DashboardCard
                            title="Location Details"
                            icon={<LocationOnIcon />}
                            accentColor="primary"
                        >
                            <Stack spacing={3}>
                                {[
                                    { field: 'name', label: 'Name', required: true },
                                    { field: 'addressLine', label: 'Address Line', required: true },
                                    { field: 'postcode', label: 'Postcode', required: true }
                                ].map(({ field, label, required }) => (
                                    <Box key={field}>
                                        <TextField
                                            fullWidth
                                            label={label}
                                            name={field}
                                            value={(chargingInfrastructure as any)[field] ?? ''}
                                            onChange={onChange}
                                            required={required}
                                            error={fieldErrors[field]}
                                            helperText={fieldErrors[field] ? `${label} is required` : ''}
                                            InputProps={field === 'postcode' ? {
                                                endAdornment: (
                                                    <IconButton
                                                        size="small"
                                                        onClick={openMapDialog}
                                                        title="Select on Map"
                                                    >
                                                        <MapIcon />
                                                    </IconButton>
                                                )
                                            } : undefined}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        </DashboardCard>
                    </Grid>

                    <Grid item xs={12}>
                        <DashboardCard
                            title="Charge Points"
                            icon={<EvStationIcon />}
                            accentColor="primary"
                        >
                            <Stack spacing={3}>
                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        onClick={() => setShowChargePointDialog(true)}
                                        disabled={isDisabled}
                                        startIcon={<EvStationIcon />}
                                    >
                                        Add Charge Point
                                    </Button>
                                </Box>

                                <ChargePointListTable
                                    chargePoints={chargingInfrastructure.chargePoints}
                                    onDeleteChargePoint={(id: string) => {
                                        setChargingInfrastructure(prev => {
                                            const updated = {
                                                ...prev,
                                                chargePoints: Array.isArray(prev.chargePoints)
                                                    ? prev.chargePoints.filter(cp => cp.id !== id)
                                                    : [],
                                            };
                                            return updated;
                                        });
                                    }}
                                />

                                <Box display="flex" justifyContent="center">
                                    <Button
                                        variant="contained"
                                        onClick={updateChargingInfrastructure}
                                        disabled={isDisabled}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </Stack>
                        </DashboardCard>
                    </Grid>
                </Grid>
            </Box>

            <Dialog
                open={showMapDialog}
                onClose={() => setShowMapDialog(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">Select Location on Map</Typography>
                </DialogTitle>
                <DialogContent>
                    {isLoaded ? (
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={selectedLocation}
                            zoom={14}
                            options={{
                                clickableIcons: false,
                                disableDefaultUI: true
                            }}
                            onClick={(e) => {
                                const lat = e.latLng?.lat();
                                const lng = e.latLng?.lng();
                                if (lat && lng) {
                                    setSelectedLocation(prev => ({ ...prev, lat, lng }));
                                    setMapWasUsed(true);
                                }
                            }}
                        >
                            <Marker
                                position={selectedLocation}
                                draggable
                                onDragEnd={(e) => {
                                    const lat = e.latLng?.lat();
                                    const lng = e.latLng?.lng();
                                    if (lat && lng) {
                                        setSelectedLocation({ lat, lng });
                                        setMapWasUsed(true);
                                    }
                                }}
                            />
                        </GoogleMap>
                    ) : (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowMapDialog(false)}>Cancel</Button>
                    <Button onClick={handleMapSelection} variant="contained">
                        Use this Location
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog 
                open={showChargePointDialog} 
                onClose={() => setShowChargePointDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">Add Charge Point</Typography>
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 2 }}>
                        <TextField label="Charger Make" value={newChargePoint.make} onChange={(e) => setNewChargePoint({ ...newChargePoint, make: e.target.value })} />
                        <TextField label="Charger Model" value={newChargePoint.model} onChange={(e) => setNewChargePoint({ ...newChargePoint, model: e.target.value })} />
                        <TextField label="EVSE ID" value={newChargePoint.evseId} onChange={(e) => setNewChargePoint({ ...newChargePoint, evseId: e.target.value })} />
                        <TextField label="CPMS Provider" value={newChargePoint.cpmsProvider} onChange={(e) => setNewChargePoint({ ...newChargePoint, cpmsProvider: e.target.value })} />
                        <TextField label="Max Rate (kW)" type="number" value={newChargePoint.maxRateKW} onChange={(e) => setNewChargePoint({ ...newChargePoint, maxRateKW: e.target.value })} />

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>Connectors</Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                                {connectorOptions.map((type) => (
                                    <Button
                                        key={type}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => {
                                            const existing = newChargePoint.connectors.find(c => c.type === type);
                                            const updated = existing
                                                ? newChargePoint.connectors.map(c => c.type === type ? { ...c, count: c.count + 1 } : c)
                                                : [...newChargePoint.connectors, { type, count: 1 }];
                                            setNewChargePoint({ ...newChargePoint, connectors: updated });
                                        }}
                                    >
                                        {type}{getCountForType(type)}
                                    </Button>
                                ))}
                            </Stack>
                        </Box>

                        <Button
                            variant="text"
                            color="secondary"
                            onClick={() => setNewChargePoint({ ...newChargePoint, connectors: [] })}
                            sx={{ alignSelf: 'flex-start' }}
                        >
                            Clear Connectors
                        </Button>
                    </Stack>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setShowChargePointDialog(false)}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            const formattedChargePoint = {
                                id: uuidv4(),
                                label: `${newChargePoint.make} ${newChargePoint.model}`,
                                evseId: newChargePoint.evseId,
                                make: newChargePoint.make,
                                model: newChargePoint.model,
                                cpmsProvider: newChargePoint.cpmsProvider,
                                maxRateKW: Number(newChargePoint.maxRateKW),
                                connectors: newChargePoint.connectors
                            };

                            setChargingInfrastructure(prev => ({
                                ...prev,
                                chargePoints: [...(prev.chargePoints || []), formattedChargePoint],
                            }));

                            setNewChargePoint({
                                make: '',
                                model: '',
                                evseId: '',
                                cpmsProvider: '',
                                maxRateKW: '',
                                connectors: []
                            });

                            setShowChargePointDialog(false);
                        }}
                    >
                        Add
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default ChargingInfrastructureEditPage;
